import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { MapPin } from 'lucide-react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type CellContext
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

const colors = {
  slate950: '#0F172A',
  slate900: '#1E293B',
  slate800: '#1E293B',
  slate700: '#334155',
  slate600: '#475569',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  emerald500: '#10B981',
  emerald400: '#34D399',
  blue400: '#60A5FA',
  red500: '#EF4444',
  red400: '#F87171',
  orange500: '#F97316',
  orange400: '#FB923C',
  yellow400: '#FACC15',
};

const ContainerCard = styled.div`
  display: flex;
  padding: 2rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  align-self: stretch;
  border-radius: 1rem;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid ${colors.slate700};
  backdrop-filter: blur(4px);
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: ${colors.slate300};
  line-height: 1.5;
  margin: 0;
  text-align: left;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid ${colors.slate700};
  border-radius: 0.5rem;
  color: white;
  font-size: 0.875rem;
  font-family: inherit;
  box-sizing: border-box;
  
  &::placeholder {
    color: ${colors.slate400};
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.blue400};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  box-sizing: border-box;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

const TableHead = styled.thead``;

const TableHeadRow = styled.tr`
  border-bottom: 1px solid ${colors.slate700};
`;

const TableHeadCell = styled.th<{ align?: string }>`
  padding: 0.75rem 1rem;
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${colors.slate300};
  white-space: normal;
  line-height: 1.4;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(51, 65, 85, 0.5);
  
  &:hover {
    background-color: rgba(51, 65, 85, 0.3);
  }
`;

const TableCell = styled.td<{ align?: string }>`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: ${colors.slate300};
  text-align: ${({ align }) => align || 'left'};
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageInfo = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const PaginatorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  padding: 0.5rem 0.75rem;
  background: ${({ disabled }) => disabled ? 'rgba(51, 65, 85, 0.3)' : 'rgba(51, 65, 85, 0.6)'};
  border: 1px solid ${colors.slate700};
  border-radius: 0.375rem;
  color: ${({ disabled }) => disabled ? colors.slate400 : 'white'};
  font-size: 0.875rem;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(51, 65, 85, 0.8);
    border-color: ${colors.slate600};
  }
`;

const PageSizeSelect = styled.select`
  padding: 0.5rem 0.75rem;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid ${colors.slate700};
  border-radius: 0.375rem;
  color: white;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${colors.blue400};
  }
  
  option {
    background: ${colors.slate900};
  }
`;

const getPunctualityColor = (value: number): string => {
  if (value >= 90) return colors.emerald400;
  if (value >= 75) return colors.yellow400;
  if (value >= 60) return colors.orange400;
  return colors.red400;
};

const getDelayColor = (value: number): string => {
  if (value <= 5) return colors.emerald400;
  if (value <= 15) return colors.yellow400;
  if (value <= 30) return colors.orange400;
  return colors.red400;
};

interface Station {
  stopName: string;
  avg_delay_minutes: number;
  punctuality_pct: number;
}

const StationsTable: React.FC<{ data: Station[] }> = ({ data }) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<any[]>([{ id: 'avg_delay_minutes', desc: true }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = useMemo<ColumnDef<Station>[]>(
    () => [
      {
        header: t('wrapped2025.tables.stations.headerStation'),
        accessorKey: "stopName",
        cell: (info: CellContext<Station, any>) => {
          return <div style={{ textAlign: 'left' }}>{info.getValue() as string}</div>;
        },
      },
      {
        header: () => (
          <span>
            {t('wrapped2025.tables.stations.headerAverageDelay')}<br />{t('wrapped2025.tables.stations.headerAverageDelayMin')}
          </span>
        ),
        accessorKey: "avg_delay_minutes",
        cell: (info: CellContext<Station, any>) => {
          const value = info.getValue();
          if (typeof value === 'number') {
            const color = getDelayColor(value);
            return (
              <span style={{ color }}>
                {value.toFixed(1)}
              </span>
            );
          }
          return '';
        },
      },
      {
        header: t('wrapped2025.tables.stations.headerPunctuality'),
        accessorKey: "punctuality_pct",
        cell: (info: CellContext<Station, any>) => {
          const value = info.getValue();
          if (typeof value === 'number') {
            const color = getPunctualityColor(value);
            return (
              <span style={{ color, fontWeight: 600 }}>
                {value.toFixed(1)}
              </span>
            );
          }
          return '';
        },
      }
    ],
    [t]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: false,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <ContainerCard>
      <CardHeader>
        <CardTitle>
          <MapPin size={24} color={colors.blue400} />
          {t('wrapped2025.tables.stations.title')}
        </CardTitle>
        <CardDescription>
          {t('wrapped2025.tables.stations.description')}
        </CardDescription>
      </CardHeader>

      <SearchInput
        placeholder={t('wrapped2025.tables.stations.filterPlaceholder')}
        value={globalFilter ?? ''}
        onChange={(e) => setGlobalFilter(e.target.value)}
      />

      <TableContainer>
        <StyledTable>
                    <TableHead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableHeadRow key={headerGroup.id}>
                                {headerGroup.headers.map((header, index) => {
                                  return (
                                    <TableHeadCell key={header.id} align="left">
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                justifyContent: 'flex-start',
                                            }}
                                            onClick={header.column.getToggleSortingHandler()}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {header.column.getCanSort() && (
                                                <span style={{ color: colors.slate400, fontSize: '0.75rem' }}>
                                                    {header.column.getIsSorted() === 'asc' && '↑'}
                                                    {header.column.getIsSorted() === 'desc' && '↓'}
                                                </span>
                                            )}
                                        </div>
                                    </TableHeadCell>
                                  );
                                })}
                            </TableHeadRow>
                        ))}
                    </TableHead>
          <TableBody>
            {table.getRowModel().rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell, index) => {
                  const isTextColumn = index === 0; // Station is text column
                  return (
                    <TableCell key={cell.id} align={isTextColumn ? 'left' : undefined}>
                      {index === 0 ? (
                        <span style={{ fontWeight: 600, color: 'white', textAlign: 'left', display: 'block' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </span>
                      ) : (
                        <div style={{ textAlign: isTextColumn ? 'left' : undefined }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>

      <CardFooter>
        <PageSizeSelect
          value={table.getState().pagination.pageSize}
          onChange={e => {
            table.setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              {pageSize} {t('wrapped2025.tables.stations.perPage')}
            </option>
          ))}
        </PageSizeSelect>
        <PageInfo>
          <span style={{ color: colors.slate300, fontSize: '0.875rem' }}>
            {t('wrapped2025.tables.stations.page')} {table.getState().pagination.pageIndex + 1} {t('wrapped2025.tables.stations.of')}{' '}
            {table.getPageCount()}
          </span>
          <PaginatorContainer>
            <PageButton
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              {t('wrapped2025.tables.stations.first')}
            </PageButton>
            <PageButton
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {t('wrapped2025.tables.stations.prev')}
            </PageButton>
            <PageButton
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {t('wrapped2025.tables.stations.next')}
            </PageButton>
            <PageButton
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              {t('wrapped2025.tables.stations.last')}
            </PageButton>
          </PaginatorContainer>
        </PageInfo>
      </CardFooter>
    </ContainerCard>
  );
};

export default StationsTable;

